import TooltipButton from "../../../../../components/tooltip/TooltipButton"
import TooltipCheckbox from "../../../../../components/tooltip/TooltipCheckbox"
import TooltipImport from "../../../../../components/tooltip/TooltipImport"
import TooltipTextfield from "../../../../../components/tooltip/TooltipTextfield"
import ManaWorld from "../../../../loop/ManaWorld"
import MGLoader from "../../../../saves/MGLoader"
import ManaParticle from "../../../ManaParticle"
import ManaNode from "../../ManaNode"
import ManaZoneNode, { ManaZoneNodeOptions } from "../../ManaZoneNode"
import NodeCategories from "../../NodeCategories"
import ManaSource from "../unique/ManaSource"

export type PortalNodeOptions = {

	mode?: PortalMode
	portalWorld?: ManaWorld
	frequency?: string

} & ManaZoneNodeOptions

enum PortalMode {
	Transmit = "Transmit",
	Receive = "Receive"
}

export default class PortalNode extends ManaZoneNode {

	static nodeType = 'PortalNode'
	static nodeName = "Portal Node"
	static nodeDesc = "Transfers/Receives particles from matching frequency"
	static nodeIcon = '🌌'
	static category = NodeCategories.Logistics

	mode
	portalWorld
	frequency

	constructor({ mode = PortalMode.Transmit, frequency = '', ...rest }: PortalNodeOptions) {
		super(rest)

		this.mode = mode
		this.frequency = frequency
		this.portalWorld = this.world
		this.symbol = this.getSymbol()
	}

	getSymbol() {
		if (this.mode === PortalMode.Transmit) return PortalNode.nodeIcon + '📤'
		return PortalNode.nodeIcon + '📥'
	}

	setMode(mode: PortalMode) {
		this.mode = mode
		this.reloadSymbol()
	}

	setFrequency(frequency: string) {
		this.frequency = frequency
		this.reloadSymbol()
	}

	getTooltip() {
		return (
			<>
				{super.getTooltip()}
				<br />
				<TooltipCheckbox
					title="Transmit"
					initialChecked={this.mode === PortalMode.Transmit}
					onChange={(checked) => {
						this.setMode(checked ? PortalMode.Transmit : PortalMode.Receive)
					}}
				/>
				<TooltipTextfield
					title="Frequency"
					initialValue={this.frequency}
					onChange={(value) => {
						this.setFrequency(value)
					}}
				/>
				<TooltipImport
					title="Import internal mana world"
					currentManaWorld={this.world as ManaWorld}
					onChange={(manaWorld) => {
						this.portalWorld = manaWorld
						this.portalWorld.getItems().forEach(item => {
							if (item instanceof ManaSource) {
								item.dispose()
							}
						})
					}}
				/>
				<TooltipButton
					variant="contained"
					disableElevation
					onClick={() => this.portalWorld = this.world}
				>
					REMOVE INTERNAL MANA WORLD
				</TooltipButton>
			</>
		)
	}

	tick(delta: number) {
		super.tick(delta)

		if (this.portalWorld && (this.portalWorld !== this.world)) {
			this.portalWorld.tick()
		}
	}

	getViableRoutes(particle: ManaParticle): ManaNode[] {
		if (this.mode === PortalMode.Transmit) {

			if (particle.portalStack && particle.portalStack.pop() === this) {

				return super.getViableRoutes(particle)

			} else {

				const viableRoutes = (this.portalWorld?.getItems().filter(node => {
					if (node instanceof PortalNode) {
						return (node.mode === PortalMode.Receive) && (node.frequency === this.frequency) && node.allowEntry(particle)
					}

					return false

				}) || []) as ManaNode[]

				if (viableRoutes.length) {
					if (!particle.portalStack) {
						particle.portalStack = []
					}

					particle.portalStack.push(this)
				}

				return viableRoutes
			}

		}

		if (particle.memory[this.uuid]) {
			particle.memory[this.uuid] = false

			const stackLen = particle.portalStack?.length

			if (stackLen) {
				return [particle.portalStack[stackLen - 1]]
			}

			return []
		} else {
			particle.memory[this.uuid] = true

			return super.getViableRoutes(particle)
		}

	}

	save() {
		return {
			...super.save(),

			mode: this.mode,
			portalWorld: Boolean(this.portalWorld && this.portalWorld !== this.world) ? MGLoader.saveMG(this.portalWorld as ManaWorld, false, true) : undefined,
			frequency: this.frequency
		}
	}

	static load(obj: any) {
		const ret = super.load(obj) as PortalNode

		if (obj.portalWorld) {

			const manaWorld = new ManaWorld(obj.world.caster, obj.world.caster.world)

			MGLoader.loadManaWorld(obj.portalWorld, manaWorld)

			ret.portalWorld = manaWorld
		} else {
			ret.portalWorld = ret.world
		}

		return ret
	}
}